import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable } from 'react-native';
import { Send, MessageCircle } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { threadKey, useChecklistThreadsStore } from '../../store/checklistThreadsStore';
import { useAuthStore } from '../../store/authStore';
import { ChecklistThreadAuthorRole, ChecklistThreadMessage } from '../../types';
import { appAlert } from '../../utils/appAlert';
import { scheduleDeferredCommit } from '../../utils/deferredCommit';
import { getErrorMessage } from '../../utils/errorHandler';
import { ContentActionMenu } from '../common';

interface ChecklistItemThreadProps {
  structureId: string;
  claimedBy?: string;
  itemId: string;
}

/**
 * Inline thread attached to a single checklist item. Visible to the structure
 * owner and any authenticated viewer (the existing rules already gate on auth);
 * author role is derived from the claim's `claimedBy`.
 */
export function ChecklistItemThread({ structureId, claimedBy, itemId }: ChecklistItemThreadProps) {
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);
  // Narrow, per-thread subscription: with N items expanded, a message landing
  // in one thread re-renders only that thread (actions are stable refs).
  const subscribe = useChecklistThreadsStore((s) => s.subscribe);
  const unsubscribe = useChecklistThreadsStore((s) => s.unsubscribe);
  const postMessage = useChecklistThreadsStore((s) => s.postMessage);
  const updateMessage = useChecklistThreadsStore((s) => s.updateMessage);
  const deleteMessage = useChecklistThreadsStore((s) => s.deleteMessage);
  const isPosting = useChecklistThreadsStore((s) => s.isPosting);

  const [draft, setDraft] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [isDraftFocused, setIsDraftFocused] = useState(false);
  // Undo toast for delete (panel-system.md Phase 4, rule 6), not a confirm —
  // same pattern as useCommentThread's handleDelete. `thread.messages` comes
  // from a live subscription (checklistThreadsStore), so this only needs to
  // filter it at render time; the real delete's own snapshot update removes
  // the row for good once the deferred commit runs.
  const [hiddenMessageIds, setHiddenMessageIds] = useState<Record<string, true>>({});

  const thread = useChecklistThreadsStore((s) => s.threads[threadKey(structureId, itemId)]);
  const messages = useMemo(
    () => (thread?.messages ?? []).filter((m) => !hiddenMessageIds[m.id]),
    [thread?.messages, hiddenMessageIds]
  );
  const isLoading = thread?.isLoading ?? true;

  useEffect(() => {
    subscribe(structureId, itemId);
    return () => unsubscribe(structureId, itemId);
  }, [structureId, itemId, subscribe, unsubscribe]);

  const authorRole: ChecklistThreadAuthorRole = useMemo(() => {
    if (!user) return 'other';
    if (claimedBy === user.id) return 'owner';
    if (user.agentProfile?.status === 'verified') return 'agent';
    return 'other';
  }, [user, claimedBy]);

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text || !user || isPosting) return;
    setDraft('');

    if (editingMessageId) {
      try {
        await updateMessage(structureId, itemId, editingMessageId, text);
        setEditingMessageId(null);
      } catch (err) {
        setDraft(text);
        appAlert('Could not update message', getErrorMessage(err));
      }
    } else {
      try {
        await postMessage(structureId, itemId, {
          authorId: user.id,
          authorName: `${user.firstName} ${user.lastName}`.trim() || user.email,
          authorAvatarUrl: user.avatarUrl,
          authorRole,
          text,
        });
      } catch (err) {
        setDraft(text); // restore on failure
        appAlert('Could not post message', getErrorMessage(err));
      }
    }
  }, [
    draft,
    user,
    isPosting,
    editingMessageId,
    postMessage,
    updateMessage,
    structureId,
    itemId,
    authorRole,
  ]);

  const handleStartEdit = useCallback((message: ChecklistThreadMessage) => {
    setEditingMessageId(message.id);
    setDraft(message.text);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setDraft('');
  }, []);

  const handleDeleteMessage = useCallback(
    (messageId: string) => {
      scheduleDeferredCommit({
        key: `checklist-message-${structureId}-${itemId}-${messageId}`,
        message: 'Message deleted',
        // A rejection here is caught by scheduleDeferredCommit itself, which
        // calls onUndo and shows its own "Couldn't remove" toast — no
        // separate appAlert needed on this path.
        commit: async () => {
          await deleteMessage(structureId, itemId, messageId);
          setHiddenMessageIds((prev) => {
            if (!(messageId in prev)) return prev;
            const next = { ...prev };
            delete next[messageId];
            return next;
          });
        },
        onHide: () => setHiddenMessageIds((prev) => ({ ...prev, [messageId]: true })),
        onUndo: () =>
          setHiddenMessageIds((prev) => {
            if (!(messageId in prev)) return prev;
            const next = { ...prev };
            delete next[messageId];
            return next;
          }),
      });
    },
    [structureId, itemId, deleteMessage]
  );

  return (
    <View style={[styles.container, { borderTopColor: colors.divider }]}>
      <View style={styles.header}>
        <MessageCircle size={14} color={colors.textSecondary} />
        <Text style={[styles.headerLabel, { color: colors.textSecondary }]}>
          Discussion {messages.length > 0 ? `· ${messages.length}` : ''}
        </Text>
      </View>

      {isLoading && messages.length === 0 ? (
        <Text style={[styles.empty, { color: colors.textTertiary }]}>Loading…</Text>
      ) : messages.length === 0 ? (
        <Text style={[styles.empty, { color: colors.textSecondary }]}>
          No messages yet. Share notes, questions, or coordinate with your agent.
        </Text>
      ) : (
        <View style={styles.messages}>
          {messages.map((m) => (
            <View key={m.id} style={styles.message}>
              <View style={styles.messageHeader}>
                <Text style={[styles.author, { color: colors.text }]}>{m.authorName}</Text>
                <RoleBadge role={m.authorRole} />
                {m.updatedAt != null && (
                  <Text style={[styles.editedLabel, { color: colors.textTertiary }]}>(edited)</Text>
                )}
                <View style={{ flex: 1 }} />
                {user && m.authorId === user.id && (
                  <ContentActionMenu
                    onEdit={() => handleStartEdit(m)}
                    onDelete={() => handleDeleteMessage(m.id)}
                    size={14}
                  />
                )}
              </View>
              <Text style={[styles.body, { color: colors.text }]}>{m.text}</Text>
            </View>
          ))}
        </View>
      )}

      {user && (
        <View
          style={[
            styles.composer,
            { borderColor: isDraftFocused ? colors.borderFocus : colors.borderStrong },
          ]}>
          <TextInput
            style={[styles.input, { color: colors.text }]}
            placeholder={editingMessageId ? 'Edit message…' : 'Add a message…'}
            placeholderTextColor={colors.textTertiary}
            value={draft}
            onChangeText={setDraft}
            onFocus={() => setIsDraftFocused(true)}
            onBlur={() => setIsDraftFocused(false)}
            onSubmitEditing={handleSend}
            returnKeyType="send"
            blurOnSubmit={false}
            maxLength={1000}
          />
          {editingMessageId && (
            <Pressable onPress={handleCancelEdit} style={styles.cancelBtn}>
              <Text style={[styles.cancelText, { color: colors.textSecondary }]}>Cancel</Text>
            </Pressable>
          )}
          <Pressable
            onPress={handleSend}
            disabled={!draft.trim() || isPosting}
            style={[
              styles.sendBtn,
              {
                backgroundColor: draft.trim() && !isPosting ? colors.primary : colors.border,
              },
            ]}>
            <Send size={14} color={colors.white} />
          </Pressable>
        </View>
      )}
    </View>
  );
}

function RoleBadge({ role }: { role: ChecklistThreadAuthorRole }) {
  const { colors } = useTheme();
  if (role === 'other') return null;
  const label = role === 'owner' ? 'Owner' : 'Agent';
  const bg = role === 'owner' ? colors.primarySoft : colors.progressSoft;
  const fg = role === 'owner' ? colors.primary : colors.progress;
  return (
    <View style={[styles.badge, { backgroundColor: bg }]}>
      <Text style={[styles.badgeText, { color: fg }]}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginBottom: SPACING.sm,
  },
  headerLabel: {
    ...TYPOGRAPHY.overline,
    letterSpacing: 1.1,
  },
  empty: {
    ...TYPOGRAPHY.bodySmall,
    fontStyle: 'italic',
    marginBottom: SPACING.sm,
  },
  messages: {
    gap: SPACING.sm,
    marginBottom: SPACING.sm,
  },
  message: {
    paddingVertical: SPACING.xs,
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginBottom: 2,
  },
  author: {
    ...TYPOGRAPHY.button,
  },
  editedLabel: {
    ...TYPOGRAPHY.bodySmall,
    fontStyle: 'italic',
  },
  badge: {
    paddingHorizontal: SPACING.xs,
    paddingVertical: 2,
    borderRadius: BORDER_RADIUS.sm,
  },
  badgeText: {
    ...TYPOGRAPHY.overline,
    letterSpacing: 0.8,
  },
  body: {
    ...TYPOGRAPHY.body,
    lineHeight: 20,
  },
  // Bordered pill on the canvas (mockup `.askbar`), not a filled gray box.
  composer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: BORDER_RADIUS.full,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    gap: SPACING.sm,
  },
  input: {
    flex: 1,
    ...TYPOGRAPHY.body,
    height: 32,
    outlineStyle: 'none' as any,
  },
  cancelBtn: {
    paddingHorizontal: SPACING.xs,
    paddingVertical: SPACING.xs,
  },
  cancelText: {
    ...TYPOGRAPHY.label,
  },
  sendBtn: {
    width: 32,
    height: 32,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
